import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import { utils } from '@makeship/core';
import { getProductFromContext } from '../../context/product';
import { ProductStage, ProductTag } from '../../types/common';
import { getStage, hasProductTag, isEligibleForTimer } from '../../utils/product';
import { S2, H5, P2 } from '../Typography';
import { useWindowSize } from '../../hooks/useWindowSize';

const CountdownTimer = dynamic(() => import('../CountdownTimer'), { ssr: false });

const ProductCountdownTimerWrapper = styled.div<{
  isLastDay?: boolean;
  backgroundColour?: string;
}>`
  background-color: ${({ backgroundColour }) => backgroundColour};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  border-radius: 12px;
  width: 344px;
`;

const CountdownTitle = styled(P2)<{ isSmallViewport: boolean; textColour?: string }>`
  color: ${({ textColour }) => textColour};
  text-align: center;
  display: ${({ isSmallViewport }) => (isSmallViewport ? `block` : `inline`)};
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    font-size: 14px;
  }
`;

const CountdownTimerWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const CountdownDate = styled(P2)<{ isSmallViewport: boolean; textColour?: string }>`
  color: ${({ textColour }) => textColour};
  text-align: center;
  display: ${({ isSmallViewport }) => (isSmallViewport ? `block` : `inline`)};
`;

type ProductPageCountdownProps = {
  currentDate: string;
  totalInventory?: number;
  selectedProduct?: Shopify.Product;
  backgroundColour?: string;
  textColour?: string;
};

const renderCountdownTitle = (
  stage: string,
  currentDate: string,
  isDateLengthTooLong: boolean,
  textColour: string | undefined,
  isLastDay?: boolean,
  isLimited?: boolean,
): React.ReactNode => {
  const date = utils.getDateLocalCustomFormat(currentDate, 'MMMM D, h:mmA z');

  if (stage === ProductStage.ComingSoon || stage === ProductStage.Draft) {
    return (
      <>
        <CountdownTitle
          data-testid="product-countdown-title"
          isSmallViewport={isDateLengthTooLong}
          textColour={textColour}
        >
          ⏰ <strong>NEW</strong> Product Drop:{' '}
        </CountdownTitle>
        <CountdownDate isSmallViewport={isDateLengthTooLong} textColour={textColour}>
          <strong>{date}</strong> ⏰
        </CountdownDate>
      </>
    );
  }

  if (isLimited) {
    return;
  }

  if (isLastDay) {
    return (
      <>
        <CountdownTitle
          data-testid="product-countdown-title"
          isSmallViewport={isDateLengthTooLong}
          textColour={textColour}
        >
          🚨 Hurry! Last chance:{' '}
        </CountdownTitle>
        <CountdownDate isSmallViewport={isDateLengthTooLong} textColour={textColour}>
          <strong>{date}</strong> 🚨
        </CountdownDate>
      </>
    );
  }
  return (
    <>
      <CountdownTitle
        data-testid="product-countdown-title"
        isSmallViewport={isDateLengthTooLong}
        textColour={textColour}
      >
        Only Available Until:{' '}
      </CountdownTitle>
      <CountdownDate isSmallViewport={isDateLengthTooLong} textColour={textColour}>
        <strong>{date}</strong>
      </CountdownDate>
    </>
  );
};

const renderCountdownTimer = (
  stage: string,
  currentDate: string,
  productTags: string[],
  isSmallViewport: boolean,
  ref: React.RefObject<HTMLDivElement | null>,
  backgroundColour?: string,
  textColour?: string,
): React.ReactNode => {
  const isLastDay = utils.getDayDiffFrom(currentDate) === 0;
  const isLimited = productTags.includes(ProductTag.Limited);

  return (
    <CountdownTimerWrapper>
      <ProductCountdownTimerWrapper isLastDay={isLastDay} backgroundColour={backgroundColour}>
        <div ref={ref}>
          {renderCountdownTitle(stage, currentDate, isSmallViewport, textColour, isLastDay, isLimited)}
        </div>
        <CountdownTimer date={currentDate} textColour={textColour} />
      </ProductCountdownTimerWrapper>
    </CountdownTimerWrapper>
  );
};

const ProductPageCountdown: React.FC<ProductPageCountdownProps> = ({
  currentDate,
  totalInventory,
  selectedProduct,
  backgroundColour,
  textColour,
}: ProductPageCountdownProps) => {
  const [isDateLengthTooLong, setIsDateLengthTooLong] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const product = selectedProduct || getProductFromContext();
  const stage = getStage(product.tags);

  const size = useWindowSize();

  useEffect(() => {
    if (ref.current) {
      if (ref.current.getBoundingClientRect().width > 322 || (size.width && size.width < 380)) {
        setIsDateLengthTooLong(true);
      }
    }
  }, [size]);

  return (
    <>
      {hasProductTag(product.tags, ProductTag.Limited) &&
        totalInventory &&
        totalInventory > 0 &&
        stage !== ProductStage.ComingSoon && (
          <ProductCountdownTimerWrapper>
            <S2>Limited Edition!</S2>
            <H5>Only {totalInventory} left!</H5>
          </ProductCountdownTimerWrapper>
        )}
      {currentDate &&
        isEligibleForTimer(product) &&
        renderCountdownTimer(stage, currentDate, product.tags, isDateLengthTooLong, ref, backgroundColour, textColour)}
    </>
  );
};

export default ProductPageCountdown;
